<template>
  <!-- Header with token badge, notifications, and user menu -->
  <div id="header" class="flex justify-between items-center gap-4 px-8 py-4">
    <!-- Dynamic header content: Logo on homepage, Bot info in bot editor -->
    <div class="flex items-center gap-2">
      <!-- Logo on homepage -->
      <img
        v-if="!isInBotEditor"
        src="/logo/full-gradient-stroke.png"
        alt="crafter logo"
        class="h-8 object-contain"
      />

      <!-- Bot name and ID in bot editor -->
      <div v-else-if="isInBotEditor" class="flex flex-col mt-2">
        <h1 class="text-4xl font-bold text-white">
          {{ botName || 'Bot' }}
        </h1>
        <p class="mt-2 text-sm text-gray-400">
          {{ $t('botEditor.header.idLabel') }}
          <span class="font-mono">{{ botId }}</span>
        </p>
      </div>
    </div>
    <!-- Right grouped controls: Token badge, notification bell, avatar + dropdown -->
    <div class="flex items-center gap-3">
      <!-- Apri Live Chat: solo se il bot ha il connettore collegato a un team -->
      <a
        v-if="liveChatHref"
        :href="liveChatHref"
        target="_blank"
        rel="noopener"
        class="flex items-center gap-2 h-9 px-3 rounded-lg text-sm whitespace-nowrap shrink-0 text-gray-300 hover:text-white hover:bg-[--p-surface-700] transition"
        data-telemetry-id="btn-header-open-livechat"
      >
        <span class="material-icons text-lg">support_agent</span>
        {{ $t('botEditor.connectors.livechat.open') }}
      </a>

      <WorkspaceSwitcher />

      <TokenBadge />

      <McpConnectButton />

      <NotificationBell />

      <!-- User Menu Component -->
      <UserMenu
        :locale="locale"
        @logout="emit('logout')"
        @change-language="(newLocale) => emit('changeLanguage', newLocale)"
      />
    </div>
  </div>

  <!-- Gradient separator -->
  <div class="h-0.5 bg-gradient-to-r from-cyan-400 via-indigo-500 to-pink-500 rounded-xs mx-8 mb-6" aria-hidden="true"></div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import TokenBadge from '../ui/TokenBadge.vue';
import NotificationBell from '../NotificationBell.vue';
import McpConnectButton from '../mcp/McpConnectButton.vue';
import WorkspaceSwitcher from '../ui/WorkspaceSwitcher.vue';
import UserMenu from './UserMenu.vue';
import { getLiveChatStatus } from '../../services/liveChatApi';
import { liveChatUrl } from '../../utils/liveChatUrl';

/**
 * Header Component
 *
 * Displays the main application header with dynamic content based on current route:
 * - Homepage (/app/bots): Shows platform logo
 * - Bot Editor (/app/bots/:id/*): Shows bot name and ID
 *
 * Also displays token information, notifications,
 * and user menu (which contains language selector and fetches user data directly).
 *
 * @component
 * @example
 * <Header
 *   :is-in-bot-editor="isBotEditor"
 *   :bot-id="currentBotId"
 *   :bot-name="currentBotName"
 *   :locale="locale"
 *   @change-language="handleLanguageChange"
 *   @logout="handleLogout"
 * />
 */

const { t } = useI18n();
const route = useRoute();

/**
 * Component props
 */
const props = defineProps({
  /**
   * Whether the current route is in bot editor
   * @type {Boolean}
   */
  isInBotEditor: {
    type: Boolean,
    default: false
  },

  /**
   * Current bot ID
   * @type {String}
   */
  botId: {
    type: String,
    default: ''
  },

  /**
   * Current bot name
   * @type {String}
   */
  botName: {
    type: String,
    default: ''
  },

  /**
   * Current locale/language code (e.g., 'it', 'en')
   * Passed through to UserMenu component
   * @type {String}
   */
  locale: {
    type: String,
    required: true
  }
});

// Link "Apri Live Chat": si aggiorna al cambio di bot/workspace, non dopo il salvataggio del dialog
const liveChatHref = ref(null);

watch(
  () => [props.isInBotEditor, props.botId, route.params.workspaceId],
  async ([inEditor, botId, workspaceId]) => {
    liveChatHref.value = null;
    if (!inEditor || !botId || !workspaceId) return;
    try {
      const status = await getLiveChatStatus(workspaceId, botId);
      // ignora risposte in ritardo se nel frattempo il bot è cambiato
      if (props.botId !== botId) return;
      liveChatHref.value = status?.enabled && status?.handoverTeamId
        ? liveChatUrl(status.handoverUrl, status.handoverTeamId)
        : null;
    } catch (error) {
      Console.error('Stato Live Chat non disponibile:', error);
    }
  },
  { immediate: true }
);

/**
 * Component events
 */
const emit = defineEmits({
  /**
   * Emitted when user changes the language (from UserMenu)
   * @param {string} locale - The selected locale code
   */
  changeLanguage: (locale) => typeof locale === 'string',

  /**
   * Emitted when user clicks logout
   */
  logout: () => true
});
</script>
